import { React, useEffect } from '../lib/imports';

import { getDisplayName } from '../lib/utils';
import { analyticActionMap } from '../analytics/utils';
import { fireMessageEvent } from '../messenger/utils';

const withTracking = (WrappedComponent) => {
  const WithTracking = ({ ...props }) => {
    const {
      config,
      onboarding,
      isFetching,
      heatStreakPanelOpen,
      currentRoute,
      correctAnswer,
      selectedAnswer,
      randomPicksSelected,
      picksSubmitted,
      isOffersClicked,
      isActive,
      reminderHasUpdated,
    } = props;

    const { platform, env } = config;

    const {
      viewedType,
      clickedType,
      modalOpenedType,
      path,
      tabType,
      randomlySelectPlayers,
      buttonType,
      userStats,
      submitPicks,
      toggleType,
      popupType,
      onValue,
      offValue,
      skillTestQuestion,
      submitElement,
      notifications,
    } = analyticActionMap;

    const { onboardingStep, hasAnswered } = onboarding;

    // Notifications change
    useEffect(() => {
      if ((currentRoute !== 'playNow' || currentRoute !== 'onboarding') && reminderHasUpdated === false) {
        return;
      }

      fireMessageEvent({
        platform,
        env,
        title: clickedType,
        data: {
          path: path,
          modal: notifications,
          element: toggleType,
          element_name: `${analyticActionMap[currentRoute]}_${notifications}_${isActive ? onValue : offValue}`,
          element_action: isActive ? onValue : offValue,
        },
      });
    }, [reminderHasUpdated, isActive]);

    // Route tracking
    useEffect(() => {
      if (currentRoute === 'onboarding' && !selectedAnswer && correctAnswer) {
        return;
      }

      if (currentRoute === 'onboarding' && !selectedAnswer) {
        fireMessageEvent({
          platform,
          env,
          title: modalOpenedType,
          data: {
            path: path,
            modal: skillTestQuestion,
            modal_type: popupType,
          },
        });
        return;
      }

      fireMessageEvent({
        platform,
        env,
        title: viewedType,
        data: {
          path: path,
          element: tabType,
          element_name: analyticActionMap[currentRoute],
        },
      });
    }, [currentRoute, correctAnswer]);

    // Quiz Tracking
    useEffect(() => {
      if (isFetching || currentRoute !== 'onboarding' || !selectedAnswer || onboardingStep !== 0 || !hasAnswered) {
        return;
      }

      fireMessageEvent({
        platform,
        env,
        title: clickedType,
        data: {
          path: path,
          modal: skillTestQuestion,
          element: buttonType,
          element_name: submitElement,
        },
      });
    }, [isFetching, correctAnswer, selectedAnswer]);

    // Player Picks Auto Selected Tracking
    useEffect(() => {
      if (isFetching || currentRoute !== 'playNow' || !randomPicksSelected) {
        return;
      }
      fireMessageEvent({
        platform,
        env,
        title: clickedType,
        data: {
          path: path,
          element: buttonType,
          element_name: randomlySelectPlayers,
        },
      });
    }, [isFetching, randomPicksSelected]);

    // Player Picks Submitted Tracking
    useEffect(() => {
      if (isFetching || currentRoute !== 'playNow' || !picksSubmitted) {
        return;
      }

      fireMessageEvent({
        platform,
        env,
        title: clickedType,
        data: {
          path: path,
          element: buttonType,
          element_name: submitPicks,
        },
      });
    }, [isFetching, picksSubmitted]);

    // HeatStreak Panel Tracking
    useEffect(() => {
      if (isFetching || !heatStreakPanelOpen) {
        return;
      }

      fireMessageEvent({
        platform,
        env,
        title: clickedType,
        data: {
          path: path,
          element: tabType,
          element_name: userStats,
        },
      });
    }, [isFetching, heatStreakPanelOpen]);

    // HeatStreak Panel Page View
    useEffect(() => {
      if (isFetching || !heatStreakPanelOpen) {
        return;
      }

      fireMessageEvent({
        platform,
        env,
        title: viewedType,
        data: {
          path: path,
          element: tabType,
          element_name: userStats,
        },
      });
    }, [isFetching, currentRoute, heatStreakPanelOpen]);

    // Offers Tracking
    useEffect(() => {
      if (isFetching || currentRoute !== 'playNow' || !isOffersClicked) {
        return;
      }

      fireMessageEvent({
        platform,
        env,
        title: 'Test!',
      });
    }, [isFetching, isOffersClicked]);

    return <WrappedComponent {...props} />;
  };

  WithTracking.displayName = `WithTracking(${getDisplayName(WrappedComponent)})`;

  return WithTracking;
};

export default withTracking;
